import { Inject, Injectable } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { LightboxService } from '../components/common/lightbox/lightbox.service';
import { PdfBoxComponent } from '../components/common/pdf-box/pdf-box.component';
import { AttachmentDto } from '../models/attachment/attachment-dto.model';
import { blobToArrayBuffer } from './attachment/attachment-helper';
import { AttachmentServiceToken, IAttachmentService } from './attachment/attachment-service.interface';

@Injectable({ providedIn: 'root' })
export class PreviewModalService {
  constructor(
    @Inject(AttachmentServiceToken) private _attachmentService: IAttachmentService,
    private _lightboxService: LightboxService,
    private _modalCtrl: ModalController,
    private _loadingController: LoadingController,
    private _translateService: TranslateService
  ) {}

  public async previewAttachment(attachment: AttachmentDto): Promise<void> {
    const loading = await this._loadingController.create({
      message: this._translateService.instant('components.swodoc.loadingPreview'),
    });
    await loading.present();
    const closeModal = async () => {
      await loading.dismiss().catch(error => console.log(error));
    };
    if (attachment?.contentType) {
      if (attachment.contentType.includes('image')) {
        // show image preview
        const imageData = await this._attachmentService.get(attachment._id, 'base64');
        await closeModal();
        this.previewImage(imageData);
      } else if (attachment.contentType.includes('pdf')) {
        // show pdf preview
        const dataBlob = await this._attachmentService.get(attachment._id, 'blob');
        await closeModal();
        this.previewPdfBlob(dataBlob, attachment.filename);
      }
    }
  }

  public async previewImage(base64Data: string, index: number = 0): Promise<void> {
    await this._lightboxService.show([base64Data], index, true);
  }

  public async previewPdfBlob(dataBlob: Blob, fileName: string): Promise<void> {
    const pdfBuffer = await blobToArrayBuffer(dataBlob);
    await this.previewPdfBuffer(pdfBuffer, fileName);
  }

  public async previewPdfBuffer(pdfBuffer: ArrayBuffer, fileName: string): Promise<void> {
    const modal = await this._modalCtrl.create({
      component: PdfBoxComponent,
      componentProps: {
        fileName: fileName,
        src: pdfBuffer,
      },
      cssClass: 'fullPageModal',
    });
    await modal.present();
  }

  public async previewPdfBase64(base64: string, fileName: string): Promise<void> {
    const pdfBuffer = await this.base64ToBufferAsync(base64);
    await this.previewPdfBuffer(pdfBuffer, '');
  }

  private async base64ToBufferAsync(base64) {
    var dataUrl = 'data:application/octet-binary;base64,' + base64.split(',')[1];

    const res = await fetch(dataUrl);
    return res.arrayBuffer();
  }
}
